/**
 * Event External Links Manager Component
 * For managing external links associated with events
 */

import React, { useState } from "react";
import Button from "../common/Button";
import Input from "../common/Input";
import Select from "../common/Select";
import Textarea from "../common/Textarea";

export interface EventLinkInput {
  id?: string;
  url: string;
  label: string;
  description?: string;
  link_type?: string;
  display_order?: number;
}

interface EventExternalLinksManagerProps {
  links: EventLinkInput[];
  onChange: (links: EventLinkInput[]) => void;
}

const LINK_TYPE_OPTIONS = [
  { value: "signup", label: "Signup Form" },
  { value: "marketing", label: "Marketing Page" },
  { value: "social", label: "Social Media" },
  { value: "video", label: "Video" },
  { value: "document", label: "Document" },
  { value: "other", label: "Other" },
];

const EventExternalLinksManager: React.FC<EventExternalLinksManagerProps> = ({
  links,
  onChange,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [currentLink, setCurrentLink] = useState<EventLinkInput>({
    url: "",
    label: "",
    description: "",
    link_type: "other",
  });

  const handleAdd = () => {
    if (!currentLink.url || !currentLink.label) {
      return;
    }

    const newLink: EventLinkInput = {
      ...currentLink,
      display_order: links.length,
    };

    onChange([...links, newLink]);
    setCurrentLink({ url: "", label: "", description: "", link_type: "other" });
    setIsAdding(false);
  };

  const handleEdit = (index: number) => {
    setCurrentLink(links[index]);
    setEditingIndex(index);
    setIsAdding(true);
  };

  const handleUpdate = () => {
    if (editingIndex === null) {
      return;
    }
    if (!currentLink.url || !currentLink.label) {
      return;
    }

    const updatedLinks = [...links];
    updatedLinks[editingIndex] = currentLink;
    onChange(updatedLinks);

    setCurrentLink({ url: "", label: "", description: "", link_type: "other" });
    setEditingIndex(null);
    setIsAdding(false);
  };

  const handleRemove = (index: number) => {
    onChange(links.filter((_, i) => i !== index));
  };

  const handleCancel = () => {
    setCurrentLink({ url: "", label: "", description: "", link_type: "other" });
    setEditingIndex(null);
    setIsAdding(false);
  };

  const moveLink = (index: number, direction: "up" | "down") => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === links.length - 1)
    ) {
      return;
    }

    const newLinks = [...links];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    [newLinks[index], newLinks[targetIndex]] = [
      newLinks[targetIndex],
      newLinks[index],
    ];

    // Update display_order
    newLinks.forEach((link, i) => {
      link.display_order = i;
    });

    onChange(newLinks);
  };

  return (
    <div className="space-y-4">
      {/* Existing Links */}
      {links.length > 0 && (
        <div className="space-y-2">
          {links.map((link, index) => (
            <div
              key={`link-${index}`}
              className="flex items-start gap-3 bg-gray-50 p-3 rounded-lg"
            >
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {link.label}
                    </p>
                    <p className="text-xs text-blue-600 break-all">
                      {link.url}
                    </p>
                    {link.description && (
                      <p className="text-xs text-gray-600 mt-1">
                        {link.description}
                      </p>
                    )}
                    {link.link_type && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded">
                        {LINK_TYPE_OPTIONS.find(
                          (opt) => opt.value === link.link_type,
                        )?.label || link.link_type}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => moveLink(index, "up")}
                  disabled={index === 0}
                  className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30"
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveLink(index, "down")}
                  disabled={index === links.length - 1}
                  className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30"
                  title="Move down"
                >
                  ↓
                </button>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleEdit(index)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form */}
      {isAdding ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-medium text-gray-900">
            {editingIndex !== null ? "Edit Link" : "Add New Link"}
          </h4>

          <Input
            label="Label"
            value={currentLink.label}
            onChange={(e) =>
              setCurrentLink({ ...currentLink, label: e.target.value })
            }
            placeholder="e.g., Event Registration"
            required
          />

          <Input
            label="URL"
            type="url"
            value={currentLink.url}
            onChange={(e) =>
              setCurrentLink({ ...currentLink, url: e.target.value })
            }
            placeholder="https://example.com/event"
            required
          />

          <Select
            label="Link Type"
            value={currentLink.link_type || "other"}
            onChange={(e) =>
              setCurrentLink({ ...currentLink, link_type: e.target.value })
            }
          >
            {LINK_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>

          <Textarea
            label="Description (Optional)"
            value={currentLink.description || ""}
            onChange={(e) =>
              setCurrentLink({ ...currentLink, description: e.target.value })
            }
            placeholder="Brief description of this link"
            rows={2}
          />

          <div className="flex gap-2">
            <Button
              type="button"
              onClick={editingIndex !== null ? handleUpdate : handleAdd}
              disabled={!currentLink.url || !currentLink.label}
            >
              {editingIndex !== null ? "Update" : "Add"}
            </Button>
            <Button type="button" variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="secondary"
          onClick={() => setIsAdding(true)}
        >
          Add External Link
        </Button>
      )}

      {links.length === 0 && !isAdding && (
        <p className="text-sm text-gray-500 italic">
          No external links added yet.
        </p>
      )}
    </div>
  );
};

export default EventExternalLinksManager;
